import React from "react";
import { View } from "react-native";
import { UpdateAiTutorInput, useGetAiTutorQuery, useUpdateAiTutorMutation } from "@axio/graphql/generated/graphql";
import InteractiveLearningFormDataContainer from "./InteractiveLearningFormDataContainer";
import type { ActivityDataInput } from "../../../components/customCoursework/ActivityForms/InteractiveLearningForm/InteractiveLearningFormCore";
import Loader from "../../../components/common/status/Loader/Loader";

type InteractiveLearningLTIEditFormContainerProps = {
  activityId: string;
  onSuccess: (ileId: string, title: string) => void;
  onError: (error: Error) => void;
  onCancel?: () => void;
  currentDomain?: string;
  imageGenerationSupportFlag?: boolean;
};

const getErrorFromUnknown = (error: unknown) => {
  if (error instanceof Error) {
    return error;
  }
  return new Error("Failed to update interactive learning experience");
};

const InteractiveLearningLTIEditFormContainer = ({
  activityId,
  onSuccess,
  onError,
  onCancel,
  currentDomain,
  imageGenerationSupportFlag = false,
}: InteractiveLearningLTIEditFormContainerProps) => {
  const {
    data,
    loading,
    error: queryError,
  } = useGetAiTutorQuery({
    variables: { id: activityId },
    skip: !activityId,
  });

  const [updateAiTutorMutation, { loading: isSubmitting }] = useUpdateAiTutorMutation();

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleSave = async (activityDataInput: unknown) => {
    try {
      const activityData = activityDataInput as ActivityDataInput;
      const aiTutorInput: UpdateAiTutorInput = {
        title: activityData.title,
        description: activityData.description || "",
        context: activityData.context || "",
        targetAudience: activityData.targetAudience || "",
        dueDate: activityData.dueDate || null,
        activityState: activityData.activityState,
        scheduledDate: activityData.scheduledDate,
        visualizationId: activityData.visualizationId ?? null,
        objectives: (activityData.objectives || []).map((obj, index) => ({
          objective: obj.objective,
          sequence: obj.sequence ?? index + 1,
          evalCriteria: obj.evalCriteria || [],
        })),
        videoPool: (activityData.videoPool || []).map((video) => ({
          videoId: video.videoId,
          objectiveText: video.objectiveText,
          objectiveSequence: video.objectiveSequence,
          isTeacherAdded: Boolean(video.isTeacherAdded),
        })),
        imagePool: (activityData.imagePool || []).map((image) => ({
          id: image.id,
          url: image.url,
          objectiveText: image.objectiveText,
          objectiveSequence: image.objectiveSequence,
          isTeacherUploaded: Boolean(image.isTeacherUploaded),
          description: image.description ?? null,
        })),
      };

      const response = await updateAiTutorMutation({
        variables: {
          id: activityId,
          aiTutor: aiTutorInput,
        },
      });

      const updateResponse = response.data?.updateAITutor;
      if (!updateResponse?.success) {
        throw new Error(updateResponse?.message || "Failed to update interactive learning experience");
      }

      const updatedIle = updateResponse.data?.[0];
      if (!updatedIle?.id) {
        throw new Error("Interactive learning experience was updated without returning an ID");
      }

      onSuccess(updatedIle.id, updatedIle.title);
    } catch (error) {
      onError(getErrorFromUnknown(error));
    }
  };

  if (loading) {
    return (
      <View style={{ marginTop: 24, alignItems: "center", justifyContent: "center" }}>
        <Loader />
      </View>
    );
  }

  if (queryError) {
    onError(new Error(queryError.message || "Failed to load interactive learning experience"));
    return null;
  }

  const aiTutor = data?.getAITutor?.data?.[0];

  if (!aiTutor) {
    onError(new Error("Interactive learning experience not found"));
    return null;
  }

  const videoPool = (aiTutor.objectives || []).flatMap((obj) =>
    (obj.videos || []).map((video) => ({
      videoId: video.videoId,
      objectiveText: obj.objective,
      objectiveSequence: obj.sequence ?? 0,
      isTeacherAdded: video.isTeacherAdded ?? false,
    }))
  );

  const initialValues: ActivityDataInput = {
    id: aiTutor.id,
    title: aiTutor.title,
    description: aiTutor.description ?? undefined,
    context: aiTutor.context ?? undefined,
    targetAudience: aiTutor.targetAudience ?? undefined,
    dueDate: aiTutor.dueDate ?? undefined,
    activityState: aiTutor.activityState ?? undefined,
    scheduledDate: aiTutor.scheduledDate ?? undefined,
    visualizationId: aiTutor.visualization?.id ?? undefined,
    videoPool,
    objectives: (aiTutor.objectives ?? []).filter(Boolean).map((obj) => ({
      id: obj.id ?? undefined,
      objective: obj.objective,
      sequence: obj.sequence ?? undefined,
      evalCriteria: obj.evalCriteria?.filter((c): c is string => c !== null),
      imagePool: (obj.imagePool ?? []).map((img) => ({
        id: img.id,
        url: img.url,
        objectiveText: img.objectiveText ?? undefined,
        objectiveSequence: img.objectiveSequence ?? undefined,
        isTeacherUploaded: img.isTeacherUploaded ?? undefined,
        description: img.description ?? null,
      })),
    })),
  };

  return (
    <InteractiveLearningFormDataContainer
      activityData={initialValues}
      onSave={handleSave}
      onCancel={handleCancel}
      isSubmitting={isSubmitting}
      currentDomain={currentDomain}
      imageGenerationSupportFlag={imageGenerationSupportFlag}
      isFromLTI={true}
    />
  );
};

export default InteractiveLearningLTIEditFormContainer;
